import {NetworkStatus} from 'apollo-client';
import React, {useCallback, useMemo, useRef, useState} from 'react';

import {
  useAccountSelectorContextProviderEntityQuery,
  useOrganizationSubscriptionBasicInfoByNameQuery,
  useViewerUserInfoQuery,
} from '../../../generated/graphql';
import {isProfilePath, useCurrentRouteMatch} from '../../../routes/utils';
import {useViewer} from '../../../state/viewer/hooks';
import {useAdminModeActive} from '../../../util/admin';
import {safeLocalStorage, safeSessionStorage} from '../../../util/localStorage';
import {useRampFlagAccountSelector} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {type Account} from '../SearchNav/types';
import {
  AccountSelectorContext,
  AccountSelectorStorageKeys,
} from './AccountSelectorContextProvider';
import {
  getAccountFromStorage,
  getCanSeeOrgInURL,
  getSelectedAccount,
} from './AccountSelectorUtils';
import {useAccountList} from './useAccountList';

type MatchParams = {
  params: {
    entityName?: string;
    orgName?: string;
  };
};

export const AccountSelectorContextProviderWrapper: React.FC<{
  children?: React.ReactNode;
}> = ({children}) => {
  const viewer = useViewer();
  const {accountList, isLoading: isAccountListLoading} = useAccountList();
  const {data: viewerData, loading: userInfoLoading} = useViewerUserInfoQuery();
  const {
    params: {orgName, entityName},
  }: MatchParams = useCurrentRouteMatch() ?? {params: {}};
  const {data: entityOrg, networkStatus: entityNetworkStatus} =
    useAccountSelectorContextProviderEntityQuery({
      variables: {teamName: entityName ?? ''},
      skip: !entityName,
    });

  // The organization associated with the orgName in the URL gets precedence over the
  // entity's org (in the event that they don't match).
  const orgNameFromCurrentUrl =
    orgName ?? entityOrg?.entity?.organization?.name;
  const skipOrgQuery = !orgNameFromCurrentUrl;
  const {data: org, networkStatus} =
    useOrganizationSubscriptionBasicInfoByNameQuery({
      variables: {name: orgNameFromCurrentUrl ?? ''},
      skip: skipOrgQuery,
    });
  const enableAccountSelector = useRampFlagAccountSelector();
  const isAdminMode = useAdminModeActive();

  const loading =
    isAccountListLoading ||
    viewer == null ||
    userInfoLoading ||
    (entityName && entityNetworkStatus !== NetworkStatus.ready) ||
    (!skipOrgQuery && networkStatus !== NetworkStatus.ready);

  const isLoaded = !loading && enableAccountSelector;

  // Track the selected account in React state so context consumers
  // re-render when the account changes. This replaces the dynamic `key`
  // prop on AccountSelectorLoadedProvider that previously caused full
  // subtree remounts.
  const [selectedAccount, setSelectedAccountState] = useState<Account | null>(
    null
  );

  const updateSelectedAccount = useCallback(
    (value: Account | null) => {
      const stringValue = JSON.stringify(value);
      safeSessionStorage.setItem(
        AccountSelectorStorageKeys.ACCOUNT_SELECTOR_VALUE,
        stringValue
      );
      safeLocalStorage.setItem(
        AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE,
        stringValue
      );
      setSelectedAccountState(value);
    },
    [setSelectedAccountState]
  );

  const updateLastNonImpersonatedAccount = useCallback(
    (value: Account | null) => {
      const stringValue = JSON.stringify(value);
      safeLocalStorage.setItem(
        AccountSelectorStorageKeys.LAST_NON_IMPERSONATED_ACCOUNT,
        stringValue
      );
    },
    []
  );

  // One-time initialization: when loading completes, compute the correct
  // selected account and sync it to session/local storage + React state.
  // Uses a ref to track the last session-storage account ID we initialized
  // against, so that manual account switches trigger re-initialization.
  const lastInitializedForRef = useRef<string | undefined>(undefined);

  if (isLoaded && viewer != null) {
    const sessionStorageAccount = getAccountFromStorage(
      safeSessionStorage,
      AccountSelectorStorageKeys.ACCOUNT_SELECTOR_VALUE,
      accountList
    );
    const sessionKey = sessionStorageAccount?.id ?? 'none';

    if (lastInitializedForRef.current !== sessionKey) {
      lastInitializedForRef.current = sessionKey;

      const computedAccount = getSelectedAccount({
        selectedAccount: sessionStorageAccount,
        lastSelectedAccount: getAccountFromStorage(
          safeLocalStorage,
          AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE,
          accountList
        ),
        isProfilePath: isProfilePath(window.location.pathname),
        isAdminMode,
        viewer,
        accountList,
        userInfo: viewerData?.viewer?.userInfo,
        orgName,
        entityName,
        orgQuery: org,
        entityQuery: entityOrg,
      });

      if (
        JSON.stringify(sessionStorageAccount) !==
        JSON.stringify(computedAccount)
      ) {
        const stringValue = JSON.stringify(computedAccount);
        safeSessionStorage.setItem(
          AccountSelectorStorageKeys.ACCOUNT_SELECTOR_VALUE,
          stringValue
        );
        safeLocalStorage.setItem(
          AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE,
          stringValue
        );
        setSelectedAccountState(computedAccount);
      } else if (
        JSON.stringify(selectedAccount) !==
        JSON.stringify(sessionStorageAccount)
      ) {
        setSelectedAccountState(sessionStorageAccount);
      }
    }
  }

  const lastNonImpersonatedAccount = isLoaded
    ? getAccountFromStorage(
        safeLocalStorage,
        AccountSelectorStorageKeys.LAST_NON_IMPERSONATED_ACCOUNT,
        accountList
      )
    : null;

  const canSeeOrgInURL = isLoaded
    ? getCanSeeOrgInURL({
        selectedAccount,
        lastSelectedAccount: getAccountFromStorage(
          safeLocalStorage,
          AccountSelectorStorageKeys.LAST_ACCOUNT_SELECTOR_VALUE,
          accountList
        ),
        isProfilePath: isProfilePath(window.location.pathname),
        isAdminMode,
        viewer: viewer!,
        accountList,
        userInfo: viewerData?.viewer?.userInfo,
        orgName,
        entityName,
        orgQuery: org,
        entityQuery: entityOrg,
      })
    : true;

  // Always render the same tree structure to avoid remounting children.
  const value = useMemo(
    () => ({
      selectedAccount: isLoaded ? selectedAccount : null,
      setSelectedAccount: updateSelectedAccount,
      lastNonImpersonatedAccount: isLoaded ? lastNonImpersonatedAccount : null,
      setLastNonImpersonatedAccount: updateLastNonImpersonatedAccount,
      canSeeOrgInURL,
      accountList,
      isAccountListLoading,
    }),
    [
      isLoaded,
      selectedAccount,
      updateSelectedAccount,
      lastNonImpersonatedAccount,
      updateLastNonImpersonatedAccount,
      canSeeOrgInURL,
      accountList,
      isAccountListLoading,
    ]
  );

  return (
    <AccountSelectorContext.Provider value={value}>
      {children}
    </AccountSelectorContext.Provider>
  );
};
